import { marked } from 'marked';
import { sanitizeHtml } from './sanitizeHtml';

/**
 * Parse markdown to HTML with safe rendering.
 * marked no longer sanitizes its output, so the generated HTML is passed
 * through DOMPurify to strip any injected scripts/handlers before it is
 * rendered via v-html.
 */
export function parseMarkdown(markdown: string): string {
  if (!markdown) return '';

  marked.setOptions({
    breaks: true,
    gfm: true,
    headerIds: true,
    mangle: false
  });
  //Replace the string '{{worker}}' with the string 'candidate'
  markdown = markdown.replace(/{{worker}}/g, 'candidate');
  return sanitizeHtml(marked(markdown) as string);
}

/**
 * Extract plain text from markdown (removes all markdown syntax)
 */
export function extractPlainText(markdown: string): string {
  if (!markdown) return '';

  // Remove markdown syntax to get plain text
  const text = markdown
    // Remove code blocks
    .replace(/```[\s\S]*?```/g, '')
    // Remove inline code
    .replace(/`([^`]+)`/g, '$1')
    // Remove images
    .replace(/!\[([^\]]*)\]\([^\)]+\)/g, '$1')
    // Remove links but keep text
    .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
    // Remove headings
    .replace(/^#{1,6}\s+/gm, '')
    // Remove bold/italic
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    // Remove blockquotes
    .replace(/^>\s+/gm, '')
    // Remove list markers
    .replace(/^[\*\-\+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    // Remove horizontal rules
    .replace(/^[\*\-_]{3,}$/gm, '')
    // Clean up extra whitespace
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return text;
}

/**
 * Generate an excerpt from markdown content
 */
export function generateExcerpt(markdown: string, maxLength = 200): string {
  const plainText = extractPlainText(markdown);

  if (plainText.length <= maxLength) {
    return plainText;
  }

  // Find a good breaking point (end of word)
  let excerpt = plainText.substring(0, maxLength);
  const lastSpace = excerpt.lastIndexOf(' ');

  if (lastSpace > maxLength * 0.8) {
    excerpt = excerpt.substring(0, lastSpace);
  }

  return excerpt + '...';
}

/**
 * Extract headings from markdown for table of contents
 */
export interface HeadingItem {
  level: number;
  text: string;
  id: string;
}

export function extractHeadings(markdown: string): HeadingItem[] {
  if (!markdown) return [];

  const headings: HeadingItem[] = [];
  const lines = markdown.split('\n');

  for (const line of lines) {
    const match = line.match(/^(#{1,6})\s+(.+)$/);
    if (match) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = generateHeadingId(text);

      // Skip H1 headings (page title)
      if (level > 1) {
        headings.push({ level, text, id });
      }
    }
  }

  return headings;
}

/**
 * Generate URL-safe ID from heading text
 */
export function generateHeadingId(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special chars
    .replace(/\s+/g, '-')     // Replace spaces with hyphens
    .replace(/-+/g, '-')      // Collapse multiple hyphens
    .replace(/^-+|-+$/g, ''); // Trim hyphens from ends
}

/**
 * Count words in markdown content
 */
export function countWords(markdown: string): number {
  const plainText = extractPlainText(markdown);
  const words = plainText.split(/\s+/).filter(word => word.length > 0);
  return words.length;
}

/**
 * Estimate reading time in minutes
 */
export function estimateReadingTime(markdown: string, wordsPerMinute = 200): number {
  const wordCount = countWords(markdown);
  return Math.ceil(wordCount / wordsPerMinute);
}
